import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createIncomeExpensive,
  getCashBookCategories,
  getCashBookItemByCategory,
  getPaymentMode,
  uploadPlayerImage,
} from "../../../services/academicService";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import { getPublicExpensiveType } from "../../../services/publicService";
import { DatePicker } from "rsuite";

const AddExpensive = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editExpensive = location.state?.user || null;
  const [uploadedImageFiles, setUploadedImageFiles] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileName, setPreviewFileName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState([]);
  const [errors, setErrors] = useState({});
  const [expensiveType, setExpensiveType] = useState([]);
  const [expensiveCategory, setExpensiveCategory] = useState([]);
  const [expensiveItems, setExpensiveItems] = useState([]);

  const [formData, setFormData] = useState({
    itemDescription: "",
    amount: "",
    expensiveType: "",
    expensiveCategoryId: "",
    paymentMode: "",
    date: null,
    comments: "",
    expensiveImage: {},
    expensiveItemId: "",
  });

  useEffect(() => {
    if (editExpensive) {
      setFormData({
        itemDescription: editExpensive.itemDescription || "",
        amount: editExpensive.amount,
        paymentMode: editExpensive.paymentMode || "Cash",
        expensiveType: editExpensive.expensiveType || "",
        expensiveCategoryId: editExpensive.expensiveCategory?.id || "",
        expensiveItemId: editExpensive.item?.id || "",
        comments: editExpensive.comments || "",
        expensiveImage: editExpensive.expensiveImage || {},
        date: editExpensive.date ? new Date(editExpensive.date + "Z") : null,
      });
      setUploadedImageFiles(editExpensive.expensiveImage || null);
    }
  }, [editExpensive]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSelectChange = (name, option) => {
    setFormData({ ...formData, [name]: option?.value || "" });
    setErrors({ ...errors, [name]: "" });
  };

  const handleImageFileChange = async (e) => {
    try {
      const file = e.target.files[0]; //single file
      if (!file) return;

      //Upload to API
      const res = await uploadPlayerImage(file);

      //REPLACE instead of append
      setUploadedImageFiles(res);
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.amount) newErrors.amount = "Amount is required";
    if (!formData.paymentMode)
      newErrors.paymentMode = "Payment Mode is required";
    if (!formData.expensiveType)
      newErrors.expensiveType = "Expensive Type is required";
    if (!formData.expensiveCategoryId)
      newErrors.expensiveCategoryId = "Expensive Category is required";
    if (!formData.expensiveItemId)
      newErrors.expensiveItemId = "Expensive Item is required";
    if (!formData.comments) {
      newErrors.comments = "Comments are required";
    }
    if (!formData.date) {
      newErrors.date = "Date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatLocalISOWithCurrentTime = (date) => {
    if (!date) return null;

    const now = new Date(); // ✅ current local time

    const pad = (n) => String(n).padStart(2, "0");

    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());

    const hh = pad(now.getHours());
    const min = pad(now.getMinutes());
    const ss = pad(now.getSeconds());

    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    const payload = {
      id: editExpensive?.id || undefined,
      amount: parseInt(formData.amount),
      // itemDescription: formData.itemDescription,
      expensiveType: formData.expensiveType,
      expensiveCategoryId: formData.expensiveCategoryId,
      itemId: formData.expensiveItemId,
      paymentMode: formData.paymentMode,
      date: formatLocalISOWithCurrentTime(formData.date),
      comments: formData.comments,
      expensiveImage: uploadedImageFiles || {},
    };

    try {
      const res = await createIncomeExpensive(payload);

      if (res) {
        toast.success(
          editExpensive
            ? "Update Income-Expensive successfully!"
            : "Income-Expensive created successfully!",
          { position: "top-right", autoClose: 1000 },
        );
      }

      navigate("/admin/all-income-expensive");
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while saving the income-expense.", {
        position: "top-right",
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMode();
    fetchExpensiveType();
    fetchCategory();
  }, []);

  async function fetchPaymentMode() {
    try {
      const res = await getPaymentMode();
      setPaymentType(res);
    } catch (error) {
      console.error("Error fetching coach skills:", error);
    }
  }

  async function fetchExpensiveType() {
    try {
      const res = await getPublicExpensiveType();
      setExpensiveType(res);
    } catch (error) {
      console.error("Error fetching coach skills:", error);
    }
  }

  async function fetchCategory() {
    try {
      const res = await getCashBookCategories();
      setExpensiveCategory(res);
    } catch (error) {
      console.error("Error fetching coach skills:", error);
    }
  }

  useEffect(() => {
    if (formData.expensiveCategoryId) {
      fetchItemsByCategory();
    }
  }, [formData.expensiveCategoryId]);

  async function fetchItemsByCategory() {
    try {
      const res = await getCashBookItemByCategory(formData.expensiveCategoryId);
      setExpensiveItems(res);
    } catch (error) {
      console.error("Error fetching coach skills:", error);
    }
  }

  const categoryOptions = expensiveCategory.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const itemOptions = expensiveItems.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  return (
    <>
      <div className="page-titles d-flex justify-content-between align-items-center">
        <h4 className="mb-0">
          {editExpensive ? "Edit" : "Add"} Income-Expensive
        </h4>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item ">
            <Link to="/admin/all-income-expensive">Income-Expensive</Link>
          </li>
          <li className="breadcrumb-item active">
            <Link> {editExpensive ? "Edit" : "Add"} Income-Expensive</Link>
          </li>
        </ol>
      </div>
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Income-Expensive Details</h5>
            </div>
            <div className="card-body">
              <form>
                <div className="row">
                  {/* Item Description */}
                  {/* <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="itemDescription">
                        Item
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="itemDescription"
                        placeholder="Enter Item Description"
                        type="text"
                        name="itemDescription"
                        className={`form-control ${
                          errors.itemDescription ? "is-invalid" : ""
                        }`}
                        value={formData.itemDescription}
                        onChange={handleChange}
                      />
                      {errors.itemDescription && (
                        <div className="text-danger m-lg-2">
                          {errors.itemDescription}
                        </div>
                      )}
                    </div>
                  </div> */}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="date">
                        Date<span className="text-danger">*</span>
                      </label>
                      <div className="input-hasicon mb-xl-0 mb-3">
                        <DatePicker
                          placeholder="Select Date"
                          className="picker-suit"
                          value={formData.date}
                          onChange={(val) =>
                            setFormData({ ...formData, date: val })
                          }
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                      {errors.date && (
                        <div className="text-danger m-lg-2">{errors.date}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Expensive Type <span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        options={expensiveType}
                        placeholder="Select Expensive Type"
                        className="custom-react-select"
                        value={expensiveType.find(
                          (option) => option.value === formData.expensiveType,
                        )}
                        onChange={(selectedOption) =>
                          setFormData({
                            ...formData,
                            expensiveType: selectedOption?.value || "",
                          })
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.expensiveType
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.expensiveType
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.expensiveType
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.expensiveType && (
                        <div className="text-danger m-lg-2">
                          {errors.expensiveType}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        options={categoryOptions}
                        placeholder="Select Category"
                        className="custom-react-select"
                        value={categoryOptions.find(
                          (option) =>
                            option.value === formData?.expensiveCategoryId,
                        )}
                        onChange={(selectedOption) => {
                          setFormData({
                            ...formData,
                            expensiveCategoryId: selectedOption?.value || "",
                            expensiveItemId: "",
                          });
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.expensiveCategoryId
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.expensiveCategoryId
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.expensiveCategoryId
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.expensiveCategoryId && (
                        <div className="text-danger m-lg-2">
                          {errors.expensiveCategoryId}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Item <span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        options={itemOptions}
                        placeholder="Select Item"
                        className="custom-react-select"
                        value={
                          formData.expensiveItemId
                            ? itemOptions.find(
                                (option) =>
                                  option.value === formData.expensiveItemId,
                              )
                            : null
                        }
                        onChange={(selectedOption) =>
                          setFormData({
                            ...formData,
                            expensiveItemId: selectedOption?.value || "",
                          })
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.expensiveItemId
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.expensiveItemId
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.expensiveItemId
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.expensiveItemId && (
                        <div className="text-danger m-lg-2">
                          {errors.expensiveItemId}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Mode */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Payment Mode <span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        options={paymentType}
                        placeholder="Select Payment Mode"
                        className="custom-react-select"
                        value={paymentType.find(
                          (option) => option.value === formData.paymentMode,
                        )}
                        onChange={(selectedOption) =>
                          setFormData({
                            ...formData,
                            paymentMode: selectedOption?.value || "",
                          })
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.paymentMode
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.paymentMode
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.paymentMode
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.paymentMode && (
                        <div className="text-danger m-lg-2">
                          {errors.paymentMode}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="amount">
                        Amount <span className="text-danger">*</span>
                      </label>
                      <input
                        id="amount"
                        name="amount"
                        placeholder="Enter Amount "
                        type="number"
                        className={`form-control ${
                          errors.amount ? "is-invalid" : ""
                        }`}
                        value={formData.amount}
                        onChange={handleChange}
                      />
                      {errors.amount && (
                        <div className="text-danger m-lg-2">
                          {errors.amount}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="comments">
                        Comments <span className="text-danger">*</span>
                      </label>
                      <input
                        id="comments"
                        name="comments"
                        placeholder="Enter Comments "
                        type="text"
                        className={`form-control ${
                          errors.comments ? "is-invalid" : ""
                        }`}
                        value={formData.comments}
                        onChange={handleChange}
                      />
                      {errors.comments && (
                        <div className="text-danger m-lg-2">
                          {errors.comments}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Parent Signature Upload */}
                  <div className="col-sm-6">
                    <div className="form-group fallback w-100">
                      <label className="form-label">Upload Image </label>
                      <input
                        id="expenseImage"
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageFileChange}
                      />
                    </div>
                  </div>

                  <div className="col-sm-12 flex-row d-flex gap-4">
                    {uploadedImageFiles && (
                      <div className="col-sm-6">
                        <h6>Image File</h6>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                            {/* File Name on the Left */}
                            <div
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {uploadedImageFiles?.fileName}
                            </div>

                            {/* Action Buttons on the Right */}
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="View"
                                onClick={() => {
                                  setPreviewFileName(
                                    uploadedImageFiles.fileName,
                                  );
                                  setPreviewModal(true);
                                }}
                              >
                                <i className="fa fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                title="Delete"
                                onClick={() => setUploadedImageFiles(null)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="btn btn-primary me-1"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                    <Link
                      to="/admin/all-income-expensive"
                      className="btn btn-danger light"
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ImagePreviewModal
        show={previewModal}
        onClose={() => setPreviewModal(false)}
        fileName={previewFileName}
      />
    </>
  );
};

export default AddExpensive;
